import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type ScheduleSettings, type HolidayInfo, getDayName, isWorkingDay } from '@/lib/scheduleUtils';

interface Props {
  show: boolean;
  includedItems: Array<{ id?: string; start_date?: string | null; end_date?: string | null; sort_order?: number; duration_days?: number; is_complete?: boolean; progress?: number }>;
  settings: ScheduleSettings;
  holidays: HolidayInfo[];
  saving: boolean;
  saveError: string;
  onClose: () => void;
  onGenerate: (startDate: string, mode: 'fill-empty' | 'overwrite-all', scope: 'all-active' | 'unfinished-only') => Promise<void>;
}

export default function ScheduleGenerateModal({
  show,
  includedItems,
  settings,
  holidays,
  saving,
  saveError,
  onClose,
  onGenerate,
}: Props) {
  const { t } = useTranslation();
  const [generateStartDate, setGenerateStartDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  const [mode, setMode] = useState<'fill-empty' | 'overwrite-all'>('fill-empty');
  const [scope, setScope] = useState<'all-active' | 'unfinished-only'>('all-active');
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);

  // Default to next working day from today
  useEffect(() => {
    if (!show) return;
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setGenerateStartDate(iso);
  }, [show]);

  if (!show) return null;

  const hasExisting = includedItems.some((i) => i.start_date || i.end_date);
  const unfinishedOnlyCount = includedItems.filter((i) => !i.is_complete && (i.progress ?? 0) < 100).length;

  const handlePrimaryAction = () => {
    if (!generateStartDate) return;
    if (mode === 'overwrite-all' && hasExisting && !showOverwriteConfirm) {
      setShowOverwriteConfirm(true);
      return;
    }
    onGenerate(generateStartDate, mode, scope);
    setShowOverwriteConfirm(false);
  };

  return (
    <>
      <div className="fixed inset-0 z-[70] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
        <div className="relative bg-white w-full max-w-md rounded-3xl overflow-hidden mx-4">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-[#1A1A1A] font-bold text-base">{t('generateModal.title')}</h3>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
              <i className="ri-close-line text-[#374151]"></i>
            </button>
          </div>

          <div className="px-5 py-4 space-y-4">
            {/* Project start date */}
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('generateModal.startDateLabel')}</label>
              <input
                type="date"
                value={generateStartDate}
                onChange={(e) => setGenerateStartDate(e.target.value)}
                className="w-full min-w-0 bg-white border border-[#E0DDD8] rounded-xl px-3 py-3 text-sm text-[#1A1A1A] block"
              />
              {generateStartDate && !isWorkingDay(generateStartDate, settings, holidays) && (
                <div className="mt-2 bg-[#FEF0E7] rounded-xl p-2.5 border border-[#FDE68A] flex items-start gap-2">
                  <i className="ri-error-warning-line text-[#F59E0B] text-sm flex-shrink-0 mt-0.5"></i>
                  <p className="text-[#92400E] text-xs">
                    {t('generateModal.nonWorkingDayWarning', { day: getDayName(generateStartDate) })}
                  </p>
                </div>
              )}
            </div>

            {/* Scope selector */}
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('generateModal.scopeLabel')}</label>
              <div className="space-y-2">
                <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${scope === 'all-active' ? 'border-[#E8621A] bg-[#FEF0E7]/30' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    name="scope"
                    checked={scope === 'all-active'}
                    onChange={() => setScope('all-active')}
                    className="mt-0.5 accent-[#E8621A]"
                  />
                  <div>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{t('generateModal.scopeAll')}</p>
                    <p className="text-xs text-[#6B7280] mt-0.5">{t('generateModal.scopeAllDesc', { count: includedItems.length })}</p>
                  </div>
                </label>

                <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${scope === 'unfinished-only' ? 'border-[#E8621A] bg-[#FEF0E7]/30' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    name="scope"
                    checked={scope === 'unfinished-only'}
                    onChange={() => setScope('unfinished-only')}
                    className="mt-0.5 accent-[#E8621A]"
                  />
                  <div>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{t('generateModal.scopeUnfinished')}</p>
                    <p className="text-xs text-[#6B7280] mt-0.5">{t('generateModal.scopeUnfinishedDesc', { count: unfinishedOnlyCount })}</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Regeneration mode */}
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('generateModal.modeLabel')}</label>
              <div className="space-y-2">
                <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${mode === 'fill-empty' ? 'border-[#E8621A] bg-[#FEF0E7]/30' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    name="regen-mode"
                    checked={mode === 'fill-empty'}
                    onChange={() => { setMode('fill-empty'); setShowOverwriteConfirm(false); }}
                    className="mt-0.5 accent-[#E8621A]"
                  />
                  <div>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{t('generateModal.modeFillEmpty')}</p>
                    <p className="text-xs text-[#6B7280] mt-0.5">{t('generateModal.modeFillEmptyDesc')}</p>
                  </div>
                </label>

                <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${mode === 'overwrite-all' ? 'border-[#E8621A] bg-[#FEF0E7]/30' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    name="regen-mode"
                    checked={mode === 'overwrite-all'}
                    onChange={() => setMode('overwrite-all')}
                    className="mt-0.5 accent-[#E8621A]"
                  />
                  <div>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{t('generateModal.modeOverwrite')}</p>
                    <p className="text-xs text-[#6B7280] mt-0.5">{t('generateModal.modeOverwriteDesc')}</p>
                    {mode === 'overwrite-all' && (
                      <p className="text-xs text-[#92400E] mt-1.5 bg-[#FEF0E7] rounded-lg px-2 py-1.5">
                        <i className="ri-alert-line mr-1"></i>
                        {t('generateModal.modeOverwriteWarning')}
                      </p>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {saveError && <p className="text-[#EF4444] text-xs bg-[#FEF2F2] rounded-xl px-3 py-2">{saveError}</p>}
          </div>

          <div className="px-5 py-4 border-t border-gray-100">
            <button
              onClick={handlePrimaryAction}
              disabled={!generateStartDate || saving}
              className="w-full py-3.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 leading-snug"
            >
              {saving ? <><i className="ri-loader-4-line animate-spin"></i>{t('generateModal.generating')}</> : t('generateModal.generateBtn')}
            </button>
          </div>
        </div>
      </div>

      {/* Overwrite Confirmation Modal */}
      {showOverwriteConfirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowOverwriteConfirm(false)}></div>
          <div className="relative bg-white w-full max-w-sm rounded-3xl overflow-hidden mx-4">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-[#1A1A1A] font-bold text-base">{t('generateModal.overwriteTitle')}</h3>
              <button onClick={() => setShowOverwriteConfirm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-[#374151]"></i>
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[#6B7280] text-sm leading-relaxed">
                {t('generateModal.overwriteDesc')}
              </p>
              <p className="text-[#92400E] text-xs bg-[#FEF0E7] rounded-xl p-2.5">
                <i className="ri-alert-line mr-1"></i>
                {t('generateModal.overwriteWarning')}
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
              <button
                onClick={() => setShowOverwriteConfirm(false)}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-[#374151] text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                {t('generateModal.cancel')}
              </button>
              <button
                onClick={() => {
                  setShowOverwriteConfirm(false);
                  onGenerate(generateStartDate, mode, scope);
                }}
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <><i className="ri-loader-4-line animate-spin"></i>...</> : t('generateModal.overwriteGenerate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}